import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowLeft, MapPin, Globe, DollarSign, Calendar } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ORDER = ["Hunting", "Fishing"];

export default function DestinationDetail() {
  const { code } = useParams();
  const [destination, setDestination] = useState(null);
  const [packages, setPackages] = useState([]);
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/destinations/${code}/packages`)
      .then((r) => {
        setDestination(r.data.destination);
        setPackages(r.data.packages);
        setSpecies(r.data.species);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [code]);

  if (loading) return <div className="min-h-screen bg-bone flex items-center justify-center pt-20"><p className="font-body text-slate">Loading destination...</p></div>;
  if (!destination) return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center pt-20 gap-4">
      <p className="font-body text-slate">Destination not found</p>
      <Link to="/expeditions" className="btn-outline-dark">Back to Expeditions</Link>
    </div>
  );

  const huntPkgs = packages.filter((p) => p.category === "Hunting");
  const fishPkgs = packages.filter((p) => p.category === "Fishing");
  const priceMin = packages.length ? Math.min(...packages.map((p) => p.price_low)) : 0;
  const priceMax = packages.length ? Math.max(...packages.map((p) => p.price_high)) : 0;

  return (
    <>
      <Helmet>
        <title>{`${destination.name} Expeditions | Dispatch Hunts`}</title>
        <meta name="description" content={`Hunting and fishing expeditions in ${destination.name}. ${packages.length} packages, ${species.length} species available.`} />
      </Helmet>

      {/* Hero */}
      <section className="section-dark grain pt-28 pb-12" data-testid="destination-hero">
        <div className="container-dh">
          <Link to="/expeditions?tab=location" className="inline-flex items-center gap-2 text-bone/60 hover:text-brass font-label text-xs tracking-wider uppercase mb-6 transition-colors" data-testid="back-to-map">
            <ArrowLeft className="w-4 h-4" /> Back to Map
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-label text-brass text-[10px]">{destination.continent}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-bone mb-4" data-testid="destination-title">{destination.name}</h1>
          <div className="flex flex-wrap gap-6 mt-6">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-brass" />
              <span className="font-body text-bone/60 text-sm">{packages.length} expedition packages</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-brass" />
              <span className="font-body text-bone/60 text-sm">{species.length} huntable species</span>
            </div>
            {priceMin > 0 && (
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-brass" />
                <span className="font-body text-bone/60 text-sm">From ${priceMin.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Species Grid */}
      <section className="section-chalk py-16" data-testid="destination-species">
        <div className="container-dh">
          <p className="text-label text-brass mb-3">Species Available</p>
          <h2 className="font-display text-2xl font-bold text-carbon mb-8">What you can hunt in {destination.name}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {species.slice(0, 20).map((s) => (
              <Link key={s.slug} to={`/expeditions/${s.slug}`} className="bg-bone border border-carbon/10 p-4 hover:border-brass/30 hover:shadow-md transition-all" data-testid={`dest-species-${s.slug}`}>
                <h4 className="font-display text-sm font-bold text-carbon mb-1">{s.name}</h4>
                <p className="text-label text-[9px] tracking-wider uppercase text-brass">{s.category}</p>
                <p className="font-mono text-xs text-slate mt-2">${s.price_low?.toLocaleString()} – ${s.price_high?.toLocaleString()}</p>
              </Link>
            ))}
            {species.length > 20 && (
              <div className="bg-bone border border-carbon/10 p-4 flex items-center justify-center">
                <p className="font-body text-sm text-smoke">+{species.length - 20} more species</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Packages by Category */}
      {huntPkgs.length > 0 && (
        <section className="section-light py-16" data-testid="destination-hunt-packages">
          <div className="container-dh">
            <p className="text-label text-brass mb-3">Hunting</p>
            <h2 className="font-display text-2xl font-bold text-carbon mb-8">{huntPkgs.length} hunting {huntPkgs.length === 1 ? "package" : "packages"} in {destination.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {huntPkgs.map((pkg) => (
                <PackageCard key={pkg.sku} pkg={pkg} />
              ))}
            </div>
          </div>
        </section>
      )}

      {fishPkgs.length > 0 && (
        <section className={`${huntPkgs.length > 0 ? "section-chalk" : "section-light"} py-16`} data-testid="destination-fish-packages">
          <div className="container-dh">
            <p className="text-label text-brass mb-3">Fishing</p>
            <h2 className="font-display text-2xl font-bold text-carbon mb-8">{fishPkgs.length} fishing {fishPkgs.length === 1 ? "package" : "packages"} in {destination.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fishPkgs.map((pkg) => (
                <PackageCard key={pkg.sku} pkg={pkg} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-dark grain py-16" data-testid="destination-cta">
        <div className="container-dh text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-bone mb-4">Plan your {destination.name} expedition</h2>
          <p className="font-body text-bone/50 mb-8">Tell us what you're after and we'll match you with the best outfitters in {destination.name}.</p>
          <Link to="/apply" className="btn-brass" data-testid="destination-apply-btn">Apply for a Consultation <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </>
  );
}

function PackageCard({ pkg }) {
  return (
    <div className="bg-bone border border-carbon/10 p-6 hover:border-brass/30 hover:shadow-lg transition-all" data-testid={`dest-pkg-${pkg.sku}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className="text-label text-brass text-[10px] tracking-wider uppercase">{pkg.region}</p>
          <h3 className="font-display text-lg font-bold text-carbon">{pkg.trip_type}</h3>
        </div>
        <p className="font-mono text-lg font-bold text-carbon">${pkg.price_low?.toLocaleString()}<span className="text-smoke text-sm">+</span></p>
      </div>
      <p className="font-body text-sm text-slate mb-3">{pkg.subcategory} — {pkg.target_species?.split(",").slice(0, 3).join(", ")}</p>
      <div className="flex items-center gap-4 text-xs text-smoke font-body mb-4">
        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{pkg.season_notes}</span>
        <span>{pkg.duration} days</span>
        <span>{pkg.difficulty}</span>
      </div>
      {pkg.highlights && <p className="font-body text-xs text-carbon/80 italic">{pkg.highlights}</p>}
      <div className="mt-4 pt-4 border-t border-carbon/10 flex justify-between items-center">
        <span className="text-label text-[9px] tracking-wider uppercase text-smoke">{pkg.sku}</span>
        <Link to="/apply" className="font-label text-xs text-brass hover:underline flex items-center gap-1">
          Apply <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
